import React, { useState, useCallback } from 'react';
import { usePollingApi, apiFetch } from '../hooks/useApi';

const BOTS = [
  { key: 'warren_buffett', label: '워런 버핏' },
  { key: 'peter_lynch', label: '피터 린치' },
  { key: 'ray_dalio', label: '레이 달리오' },
  { key: 'cathie_wood', label: '캐시 우드' },
  { key: 'jesse_livermore', label: '제시 리버모어' },
];

export default function OpsPanel() {
  const { data: health } = usePollingApi('/api/health', 60000); // 60초
  const [running, setRunning] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const runBot = async (botName, market) => {
    const key = `${botName}-${market}`;
    if (running[key]) return;
    setRunning((p) => ({ ...p, [key]: true }));
    try {
      const data = await apiFetch(`/api/run/${botName}?market=${market}`, { method: 'POST' });
      showToast(`${data.message || `${botName} 완료`}`);
    } catch (err) {
      showToast(`${botName} 실행 실패: ${err.message}`, 'error');
    } finally {
      setRunning((p) => ({ ...p, [key]: false }));
    }
  };

  const runAll = async (market) => {
    for (const bot of BOTS) {
      await runBot(bot.key, market);
    }
  };

  const isAnyRunning = Object.values(running).some(Boolean);

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700">
      <div className="p-3 border-b border-gray-700">
        <h3 className="text-sm font-semibold text-gray-400">운영</h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-700">
        {/* API 상태 */}
        <div className="p-3">
          <h4 className="text-xs text-gray-500 mb-2">API 상태</h4>
          <div className="space-y-1.5">
            <StatusRow label="KIS API" ok={health?.kis} />
            <StatusRow label="Claude" ok={health?.claude} />
            <StatusRow label="Telegram" ok={health?.telegram} />
            <StatusRow label="DART API" ok={health?.dart} />
            <StatusRow label="ECOS API" ok={health?.ecos} />
            <StatusRow label="Naver API" ok={health?.naver} />
            <StatusRow label="Yahoo Finance" ok={health?.yfinance} />
            <StatusRow label="Scheduler" ok={health?.scheduler} />
          </div>
          {health?.market_status && (
            <div className="mt-3 pt-2 border-t border-gray-700 text-xs text-gray-500">
              서버 시각: {health.timestamp}
            </div>
          )}
        </div>

        {/* 수동 실행 */}
        <div className="p-3">
          <h4 className="text-xs text-gray-500 mb-2">수동 실행</h4>
          <div className="space-y-1.5">
            {BOTS.map((bot) => (
              <div key={bot.key} className="flex items-center gap-2">
                <span className="text-xs flex-1 truncate">{bot.label}</span>
                <RunBtn label="KR" loading={running[`${bot.key}-KR`]} onClick={() => runBot(bot.key, 'KR')} />
                <RunBtn label="US" loading={running[`${bot.key}-US`]} onClick={() => runBot(bot.key, 'US')} />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3 pt-2 border-t border-gray-700">
            <button
              disabled={isAnyRunning}
              onClick={() => runAll('KR')}
              className="flex-1 px-3 py-2 bg-green-700 hover:bg-green-600 disabled:opacity-50 rounded text-xs min-h-[44px] transition"
            >
              {isAnyRunning ? <Spinner /> : '한국장 전체'}
            </button>
            <button
              disabled={isAnyRunning}
              onClick={() => runAll('US')}
              className="flex-1 px-3 py-2 bg-blue-700 hover:bg-blue-600 disabled:opacity-50 rounded text-xs min-h-[44px] transition"
            >
              {isAnyRunning ? <Spinner /> : '미국장 전체'}
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-xl text-sm z-50 transition-all ${
          toast.type === 'error' ? 'bg-red-800 text-red-100' : 'bg-green-800 text-green-100'
        }`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function StatusRow({ label, ok }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-gray-300">{label}</span>
      <span className={`flex items-center gap-1 ${ok ? 'text-green-400' : 'text-red-400'}`}>
        <span className={`w-2 h-2 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'}`} />
        {ok ? '온라인' : '오프라인'}
      </span>
    </div>
  );
}

function RunBtn({ label, loading, onClick }) {
  return (
    <button
      disabled={loading}
      onClick={onClick}
      className={`px-2 py-1 rounded text-xs min-w-[36px] min-h-[32px] transition ${
        label === 'US' ? 'bg-blue-800 hover:bg-blue-700' : 'bg-green-800 hover:bg-green-700'
      } disabled:opacity-50`}
    >
      {loading ? <Spinner /> : label}
    </button>
  );
}

function Spinner() {
  return (
    <span className="inline-block w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
  );
}
